import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { functionsService } from "@/services/functionsService";
import { firestoreService } from "@/services/firestoreService";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { MultiEarningsSelector } from "@/components/MultiEarningsSelector";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface CopilotChat {
  id: string;
  userId: string;
  messages: Array<{
    role: "user" | "assistant";
    content: string;
    timestamp: any;
  }>;
  updatedAt: any;
}

export const Copilot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [multiEarningsMode, setMultiEarningsMode] = useState(false);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { balance, refreshBalance } = useCredits();
  const { user } = useAuth();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load chat history on mount
  useEffect(() => {
    const loadChatHistory = async () => {
      if (!user) {
        setLoadingHistory(false);
        return;
      }

      try {
        setLoadingHistory(true);
        const chatDoc = await firestoreService.getDocument<CopilotChat>(
          "copilotChats",
          user.uid
        );

        if (chatDoc && chatDoc.messages) {
          const loadedMessages: Message[] = chatDoc.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp?.toDate
              ? msg.timestamp.toDate()
              : new Date(msg.timestamp || Date.now()),
          }));
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error("Failed to load chat history:", error);
      } finally {
        setLoadingHistory(false);
      }
    };

    loadChatHistory();
  }, [user]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const context = multiEarningsMode && selectedTranscriptIds.length > 0
        ? { transcriptIds: selectedTranscriptIds }
        : undefined;

      const result = await functionsService.askCopilot(input, context);

      const assistantMessage: Message = {
        role: "assistant",
        content: result.answer,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await refreshBalance();
    } catch (error: any) {
      const errorMessage: Message = {
        role: "assistant",
        content: `Error: ${
          error.message || "Failed to get response. Please try again."
        }`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="h1 text-4xl mb-2">Investor Copilot</h1>
              <p className="body-text text-gray-600">
                Ask questions about companies, earnings, and market trends. Each
                question costs 3 credits.
              </p>
              <p className="body-text text-sm text-gray-500 mt-2">
                Your credits:{" "}
                <span className="font-medium text-blazerex-green">{balance}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={multiEarningsMode}
                    onChange={(e) => {
                      setMultiEarningsMode(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedTranscriptIds([]);
                      }
                    }}
                    className="w-5 h-5 text-blazerex-green rounded focus:ring-blazerex-green cursor-pointer"
                  />
                  {multiEarningsMode && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-5 h-5 transition-colors ${
                      multiEarningsMode ? "text-blazerex-green" : "text-gray-400"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                  <span
                    className={`body-text text-sm font-medium transition-colors ${
                      multiEarningsMode ? "text-blazerex-green" : "text-gray-700"
                    }`}
                  >
                    Multi-Earnings Mode
                  </span>
                  {multiEarningsMode && selectedTranscriptIds.length > 0 && (
                    <span className="px-2 py-0.5 bg-blazerex-green text-white text-xs font-bold rounded-full">
                      {selectedTranscriptIds.length}
                    </span>
                  )}
                </div>
              </label>
            </div>
          </div>

          {multiEarningsMode && (
            <Card className="mb-4 p-4 border-2 border-blazerex-green/30 bg-gradient-to-r from-blazerex-green/5 to-transparent">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-blazerex-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                  <h3 className="sub-header text-blazerex-green">Select Earnings Calls to Compare</h3>
                </div>
                {selectedTranscriptIds.length > 0 && (
                  <div className="text-sm text-gray-600">
                    {selectedTranscriptIds.length} selected
                  </div>
                )}
              </div>
              <MultiEarningsSelector
                selectedIds={selectedTranscriptIds}
                onSelectionChange={setSelectedTranscriptIds}
              />
              {selectedTranscriptIds.length > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick Comparison Questions:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInput("Compare revenue growth between these quarters");
                      }}
                      disabled={loading || balance < 3}
                    >
                      Compare Revenue
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInput("What are the key differences in guidance between these quarters?");
                      }}
                      disabled={loading || balance < 3}
                    >
                      Compare Guidance
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInput("How have the risks and opportunities changed?");
                      }}
                      disabled={loading || balance < 3}
                    >
                      Compare Risks
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInput("Summarize the main changes and trends across these earnings calls");
                      }}
                      disabled={loading || balance < 3}
                    >
                      Full Comparison
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        <Card className="h-[600px] flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {loadingHistory ? (
              <div className="text-center text-gray-500 py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blazerex-green mx-auto mb-4"></div>
                <p className="body-text">Loading chat history...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <p className="body-text">
                  {multiEarningsMode && selectedTranscriptIds.length > 0
                    ? `Ready to compare ${selectedTranscriptIds.length} earnings call${selectedTranscriptIds.length > 1 ? "s" : ""}!`
                    : "Start a conversation by asking a question below."}
                </p>
                <p className="body-text text-sm mt-2">
                  {multiEarningsMode && selectedTranscriptIds.length > 0
                    ? "Ask questions like 'Compare revenue growth' or 'What changed between these quarters?'"
                    : 'Try: "Summarize last quarter\'s results for Apple" or "Compare Q2 guidance to Q1"'}
                </p>
              </div>
            ) : (
              messages.map((message, index) => {
                // Check if the previous user message was sent with multi-earnings context
                // We'll track this by checking if multi-earnings mode is currently active
                // (In a real implementation, you might want to store this in the message metadata)
                const isMultiEarningsContext =
                  message.role === "user" &&
                  multiEarningsMode &&
                  selectedTranscriptIds.length > 0;

                return (
                  <div
                    key={index}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div className="max-w-[80%]">
                      {isMultiEarningsContext && (
                        <div className="mb-1 text-xs text-gray-500 flex items-center gap-1 justify-end">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                          Comparing {selectedTranscriptIds.length} earnings call
                          {selectedTranscriptIds.length > 1 ? "s" : ""}
                        </div>
                      )}
                      <div
                        className={`rounded-lg p-4 ${
                          message.role === "user"
                            ? "bg-blazerex-green text-blazerex-black"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        <p className="body-text whitespace-pre-wrap">
                          {message.content}
                        </p>
                        <p className="text-xs mt-2 opacity-70">
                          {message.timestamp instanceof Date
                            ? message.timestamp.toLocaleTimeString()
                            : new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    <span className="body-text text-sm text-gray-600">
                      Thinking...
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-gray-200 p-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex gap-2"
            >
              <Input
                type="text"
                placeholder="Ask a question about companies, earnings, or markets... (Use Shift+Enter for new line)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={loading || balance < 3}
                className="flex-1"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setInput(
                      "Summarize the key points from the latest earnings call"
                    );
                  }}
                  disabled={loading || balance < 3}
                  title="Quick question: Summarize earnings"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    if (
                      multiEarningsMode &&
                      selectedTranscriptIds.length > 1
                    ) {
                      setInput(
                        "Compare these earnings calls across quarters. What changed in results, tone, and guidance?"
                      );
                    } else {
                      setInput(
                        "Compare this quarter's results to the previous quarter, including revenue, margins, and management guidance."
                      );
                    }
                  }}
                  disabled={loading || balance < 3}
                  title="Quick question: Compare quarters"
                  className="shrink-0 px-3"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !input.trim() || balance < 3}
                >
                  Send
                </Button>
              </div>
            </form>
            {balance < 3 && (
              <p className="text-sm text-red-600 mt-2">
                Insufficient credits. You need at least 3 credits to ask a
                question.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
